import {
  EmployeePaymentMethodState,
  EmployeePaymentMethodType,
  MedicalPlanDto,
  PaymentsProvider,
} from '@zorro/clients';
import { mapPlanToPaymentMethodType } from '@zorro/common-utils';
import { $enum } from 'ts-enum-util';

import { PaymentMethodStatus } from './usePaymentMethodForm';

export function derivePaymentMethodTypeFromBenefit(
  plan: Pick<MedicalPlanDto, 'premium' | 'selfPayAmount'>
): EmployeePaymentMethodType {
  return mapPlanToPaymentMethodType(plan);
}

export function canGeneratePaymentMethod(
  paymentMethodType?: EmployeePaymentMethodType,
  paymentMethodState?: EmployeePaymentMethodState
) {
  if (!paymentMethodType) return false;

  const typesSupportingGeneration = [
    EmployeePaymentMethodType.ZORRO_PAY,
    EmployeePaymentMethodType.COMBINED,
  ];

  return (
    typesSupportingGeneration.includes(paymentMethodType) &&
    paymentMethodState === EmployeePaymentMethodState.CAN_BE_GENERATED
  );
}

export function generateSubmitLabel(status: PaymentMethodStatus): string {
  return $enum.mapValue(status).with({
    [PaymentMethodStatus.LOADING]: 'Loading...',
    [PaymentMethodStatus.CONFIRM_AND_GENERATE]:
      'Confirm and generate payment method',
    [PaymentMethodStatus.CONFIRM_CHANGED_METHOD]: 'Confirm payment method',
    [PaymentMethodStatus.CONTINUE]: 'Continue',
    [PaymentMethodStatus.WAITING_FOR_ACTIVATION]: 'Activating your account...',
    [PaymentMethodStatus.TRANSFER_FUNDS]: 'Transfer funds',
  });
}

function getLynxPaymentMethodStatus(
  isAccountActive?: boolean,
  paymentMethodType?: EmployeePaymentMethodType,
  hasChangedPaymentMethodType?: boolean,
  hasExistingAllocation?: boolean
): PaymentMethodStatus {
  if (!paymentMethodType) return PaymentMethodStatus.LOADING;

  // Self-pay moves no funds through Zorro Pay, so activation doesn't gate it
  if (paymentMethodType === EmployeePaymentMethodType.SELF_PAY) {
    return hasChangedPaymentMethodType
      ? PaymentMethodStatus.CONFIRM_CHANGED_METHOD
      : PaymentMethodStatus.CONTINUE;
  }

  // Funds were already transferred for this benefit — just continue, don't
  // transfer again
  if (hasExistingAllocation) return PaymentMethodStatus.CONTINUE;

  if (isAccountActive === undefined) return PaymentMethodStatus.LOADING;
  return isAccountActive
    ? PaymentMethodStatus.TRANSFER_FUNDS
    : PaymentMethodStatus.WAITING_FOR_ACTIVATION;
}

function getEchoPaymentMethodStatus(
  paymentMethodDetails?: { state: EmployeePaymentMethodState },
  paymentMethodType?: EmployeePaymentMethodType,
  hasChangedPaymentMethodType?: boolean
): PaymentMethodStatus {
  if (!paymentMethodDetails) return PaymentMethodStatus.LOADING;
  if (canGeneratePaymentMethod(paymentMethodType, paymentMethodDetails.state))
    return PaymentMethodStatus.CONFIRM_AND_GENERATE;
  if (hasChangedPaymentMethodType)
    return PaymentMethodStatus.CONFIRM_CHANGED_METHOD;
  return PaymentMethodStatus.CONTINUE;
}

export function getPaymentMethodStatusType({
  paymentsProvider,
  isAccountActive,
  paymentMethodDetails,
  paymentMethodType,
  hasChangedPaymentMethodType,
  hasExistingAllocation,
}: {
  paymentsProvider: PaymentsProvider | undefined;
  isAccountActive?: boolean;
  paymentMethodDetails?: { state: EmployeePaymentMethodState };
  paymentMethodType?: EmployeePaymentMethodType;
  hasChangedPaymentMethodType: boolean;
  hasExistingAllocation?: boolean;
}): PaymentMethodStatus {
  if (!paymentsProvider) return PaymentMethodStatus.LOADING;
  return $enum.visitValue(paymentsProvider).with({
    [PaymentsProvider.LYNX]: () =>
      getLynxPaymentMethodStatus(
        isAccountActive,
        paymentMethodType,
        hasChangedPaymentMethodType,
        hasExistingAllocation
      ),
    [PaymentsProvider.ECHO]: () =>
      getEchoPaymentMethodStatus(
        paymentMethodDetails,
        paymentMethodType,
        hasChangedPaymentMethodType
      ),
  });
}
